import React from 'react';
import { Shield, ArrowLeft, FileText, Lock, Eye, Database, Mail, Phone, MapPin, Printer } from 'lucide-react';

export default function PrivacyPolicy({ onClose }) {
  const lastUpdated = "October 26, 2025";

  const handleBackClick = () => {
    // If onClose function is provided (modal mode), use it
    // Otherwise fall back to browser history
    if (onClose) {
      onClose();
    } else {
      window.history.back();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-emerald-50">
      {/* Header */}
      <header className="bg-emerald-800 text-white py-6 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={handleBackClick}
            className="inline-flex items-center gap-2 text-white hover:text-emerald-100 transition-colors mb-4 cursor-pointer"
          >
            <ArrowLeft size={20} />
            Back to Home
          </button>
          <div className="flex items-center gap-3">
            <Shield size={40} />
            <div>
              <h1 className="text-3xl font-bold">Privacy Policy</h1>
              <p className="text-emerald-100 text-sm">Guardian - Fall River Pharmacy</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 mb-6">
          <div className="mb-6 p-4 bg-emerald-50 border-l-4 border-emerald-700 rounded">
            <p className="text-sm text-gray-700">
              <strong>Last Updated:</strong> {lastUpdated}
            </p>
          </div>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-emerald-800 mb-4 flex items-center gap-2">
              <FileText size={28} />
              Introduction
            </h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Guardian - Fall River Pharmacy ("we," "our," or "us") is committed to protecting your privacy 
              and maintaining the confidentiality of your personal and health information. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you visit our pharmacy 
              located at 3084 Hwy 2, Fall River, Nova Scotia, B2T 1J5, or use our website.
            </p>
            <p className="text-gray-700 mb-4 leading-relaxed">
              We comply with applicable Canadian privacy legislation, including the Personal Information Protection 
              and Electronic Documents Act (PIPEDA), Nova Scotia's Personal Health Information Act (PHIA), and the 
              regulations governing pharmacy practice in Nova Scotia.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-emerald-800 mb-4 flex items-center gap-2">
              <Database size={28} />
              Information We Collect
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Personal Information</h3>
            <p className="text-gray-700 mb-3">We may collect the following types of personal information:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
              <li>Name, date of birth, and contact information (address, phone number, email)</li>
              <li>Nova Scotia Health Card number</li>
              <li>Insurance information (private or provincial health coverage)</li>
              <li>Emergency contact information</li>
              <li>Billing and payment information</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Health Information</h3>
            <p className="text-gray-700 mb-3">As a healthcare provider, we collect and maintain health information including:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
              <li>Prescription information and medication history</li>
              <li>Medical conditions, allergies, and adverse drug reactions</li>
              <li>Information from your physicians and other healthcare providers</li>
              <li>Laboratory test results and immunization records</li>
              <li>Clinical notes and pharmacy consultation records</li>
              <li>Information related to pharmaceutical care services we provide</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Website Usage Information</h3>
            <p className="text-gray-700 mb-3">When you visit our website, we may automatically collect:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
              <li>Browser type and version</li>
              <li>Device information and IP address</li>
              <li>Pages visited and time spent on our website</li>
              <li>Referring website addresses</li>
              <li>Cookie data (see Cookies section below)</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-emerald-800 mb-4 flex items-center gap-2">
              <Eye size={28} />
              How We Use Your Information
            </h2>
            <p className="text-gray-700 mb-3">We use your personal and health information for the following purposes:</p>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Pharmaceutical Care</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
              <li>Dispensing prescriptions and providing medication counseling</li>
              <li>Maintaining medication profiles and monitoring for drug interactions</li>
              <li>Conducting medication reviews and clinical assessments</li>
              <li>Providing immunizations and other clinical services</li>
              <li>Communicating with your physicians and healthcare team</li>
              <li>Participating in quality assurance and professional practice review programs</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-2">Administrative Purposes</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
              <li>Processing insurance claims and billing</li>
              <li>Maintaining accurate patient records as required by law</li>
              <li>Responding to your inquiries and providing customer service</li>
              <li>Managing loyalty programs and promotional offers (with your consent)</li>
              <li>Complying with legal and regulatory requirements</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-2">Website Improvement</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
              <li>Improving our website functionality and user experience</li>
              <li>Analyzing website traffic and usage patterns</li>
              <li>Preventing fraud and ensuring website security</li>
            </ul>
          </section>

          {/* Disclosure of Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-emerald-800 mb-4 flex items-center gap-2">
              <Lock size={28} />
              Disclosure of Information
            </h2>
            <p className="text-gray-700 mb-3">
              We respect your privacy and will only disclose your personal and health information in accordance 
              with applicable laws. We may share your information with:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
              <li><strong>Healthcare Providers:</strong> Your physicians, specialists, and other healthcare professionals 
              involved in your care (with your consent or as required for treatment)</li>
              <li><strong>Insurance Companies:</strong> Provincial health plans and private insurance companies for claims processing</li>
              <li><strong>Pharmacy Network:</strong> Other Guardian pharmacies when you transfer prescriptions or access services</li>
              <li><strong>Service Providers:</strong> Third-party vendors who assist with pharmacy management systems, 
              billing, and IT services (bound by confidentiality agreements)</li>
              <li><strong>Drug Information Services:</strong> Provincial drug programs and monitoring systems 
              (e.g., Nova Scotia Prescription Monitoring Program)</li>
              <li><strong>Regulatory Bodies:</strong> Nova Scotia College of Pharmacists and other regulatory authorities 
              when required by law</li>
              <li><strong>Legal Authorities:</strong> Law enforcement or government agencies when required by law, 
              court order, or to protect health and safety</li>
            </ul>
            <p className="text-gray-700 mt-4">
              We will not sell, rent, or trade your personal or health information to third parties for marketing purposes.
            </p>
          </section>

          {/* Cookies and Tracking */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-emerald-800 mb-4">Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 mb-3">
              Our website uses cookies and similar tracking technologies to enhance your browsing experience. 
              Cookies are small text files stored on your device that help us:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
              <li>Remember your preferences and settings</li>
              <li>Understand how visitors use our website</li>
              <li>Improve website performance and functionality</li>
              <li>Provide relevant content and information</li>
            </ul>
            <p className="text-gray-700 mb-3">
              You can control cookie preferences through your browser settings. However, disabling cookies may 
              limit certain features of our website. By using our website, you consent to our use of cookies 
              as described in this policy.
            </p>
          </section>

          {/* Data Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-emerald-800 mb-4">Data Security</h2>
            <p className="text-gray-700 mb-3">
              We implement appropriate physical, technical, and administrative safeguards to protect your 
              personal and health information from unauthorized access, disclosure, alteration, or destruction. 
              Our security measures include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
              <li>Secure storage of physical and electronic records</li>
              <li>Encrypted data transmission and storage</li>
              <li>Access controls and authentication procedures</li>
              <li>Regular security assessments and updates</li>
              <li>Employee training on privacy and confidentiality</li>
              <li>Secure disposal of records when no longer needed</li>
            </ul>
            <p className="text-gray-700">
              While we strive to protect your information, no method of transmission or storage is 100% secure. 
              We cannot guarantee absolute security but are committed to maintaining industry-standard protections.
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-emerald-800 mb-4">Data Retention</h2>
            <p className="text-gray-700 mb-3">
              We retain your personal and health information in accordance with:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
              <li>Nova Scotia College of Pharmacists' record retention requirements</li>
              <li>Provincial and federal legislation</li>
              <li>Insurance and billing requirements</li>
              <li>Our legitimate business needs</li>
            </ul>
            <p className="text-gray-700">
              Pharmacy records are typically retained for a minimum of 10 years from the date of the last 
              prescription entry or patient interaction, as required by the Nova Scotia College of Pharmacists. 
              Records may be retained longer if required by law or ongoing treatment relationships.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-emerald-800 mb-4">Your Privacy Rights</h2>
            <p className="text-gray-700 mb-3">Under Canadian privacy legislation, you have the following rights:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
              <li><strong>Access:</strong> Request access to your personal and health information we hold</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Consent Withdrawal:</strong> Withdraw consent for certain uses of your information 
              (subject to legal and contractual restrictions)</li>
              <li><strong>Complaints:</strong> File a complaint about our privacy practices with our Privacy Officer 
              or the Privacy Commissioner of Canada</li>
              <li><strong>Information:</strong> Receive information about our privacy practices and how we handle your data</li>
            </ul>
            <p className="text-gray-700 mb-3">
              Please note that certain information cannot be deleted or modified if it is required for legal, 
              regulatory, or treatment purposes. We may also be required to retain information for a specified 
              period under professional practice standards.
            </p>
          </section>

          {/* Third-Party Links */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-emerald-800 mb-4">Third-Party Websites</h2>
            <p className="text-gray-700">
              Our website may contain links to third-party websites, including healthcare resources and service 
              providers. We are not responsible for the privacy practices or content of these external sites. 
              We encourage you to review the privacy policies of any third-party websites you visit.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-emerald-800 mb-4">Children's Privacy</h2>
            <p className="text-gray-700">
              Our website is not directed to children under the age of 13, and we do not knowingly collect 
              personal information from children online. If we become aware that we have collected information 
              from a child under 13 without parental consent, we will take steps to delete that information. 
              For pharmacy services to minors, we follow applicable laws and professional standards regarding 
              consent and confidentiality.
            </p>
          </section>

          {/* Changes to Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-emerald-800 mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, 
              legal requirements, or other factors. We will post the updated policy on our website with a revised 
              "Last Updated" date. We encourage you to review this policy periodically. Your continued use of our 
              services after changes are posted constitutes your acceptance of the updated policy.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-emerald-800 mb-4 flex items-center gap-2">
              <Mail size={28} />
              Contact Us
            </h2>
            <p className="text-gray-700 mb-4">
              If you have questions about this Privacy Policy, wish to exercise your privacy rights, or have 
              concerns about how we handle your information, please contact us:
            </p>
            
            <div className="bg-emerald-50 rounded-lg p-6 border border-emerald-200">
              <h3 className="font-semibold text-gray-800 mb-3">Guardian - Fall River Pharmacy</h3>
              <div className="space-y-2 text-gray-700">
                <p className="flex items-center gap-2">
                  <MapPin size={18} className="text-emerald-700 flex-shrink-0" />
                  <span>3084 Hwy 2, Fall River, Nova Scotia, B2T 1J5</span>
                </p>
                <p className="flex items-center gap-2">
                  <Phone size={18} className="text-emerald-700 flex-shrink-0" />
                  <span>Phone: <a href="tel:9028612020" className="text-emerald-700 hover:underline">902-861-2020</a></span>
                </p>
                <p className="flex items-center gap-2">
                  <Printer size={18} className="text-emerald-700 flex-shrink-0" />
                  <span>Fax: 902-861-4498</span>
                </p>
                <p className="mt-4 text-sm">
                  <strong>Privacy Officer:</strong> Please ask for the Privacy Officer when contacting us regarding 
                  privacy matters.
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
              <p className="text-sm text-gray-700">
                <strong>Additional Resources:</strong>
              </p>
              <ul className="text-sm text-gray-700 mt-2 space-y-1">
                <li>• Office of the Privacy Commissioner of Canada: <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.priv.gc.ca</a></li>
                <li>• Nova Scotia College of Pharmacists: <a href="https://www.nspharmacists.ca" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.nspharmacists.ca</a></li>
              </ul>
            </div>
          </section>

          {/* Consent */}
          <section className="mb-8">
            <div className="bg-emerald-800 text-white rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Your Consent</h2>
              <p className="leading-relaxed">
                By providing your personal and health information to us and by using our services and website, 
                you consent to the collection, use, and disclosure of your information as described in this 
                Privacy Policy. If you have concerns about any aspect of this policy, please contact us to 
                discuss your concerns before providing your information or using our services.
              </p>
            </div>
          </section>
        </div>

        {/* Back to Top Button */}
        <div className="text-center mb-8">
          <button
            onClick={() => {
              // Scroll the modal container to the top (if in modal) or window
              const modalContainer = document.getElementById('privacy-modal-container');
              if (modalContainer) {
                modalContainer.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            className="bg-emerald-700 text-white px-6 py-3 rounded-lg hover:bg-emerald-800 transition-colors duration-200 shadow-md"
          >
            Back to Top
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} Guardian - Fall River. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}